import {
    Button,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerHeader,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
} from '@heroui/react';
import { useSession } from '@/providers/enhanced-session';
import { useState } from 'react';

// Define a type that matches our extended session structure
// This is a workaround when module augmentation doesn't work correctly
interface ExtendedSession {
    expires: string;
    user: {
        id: string;
        email: string;
        name?: string;
        image?: string;
        groups: string[];
        role: string;
        isAdmin: boolean;
        isInternal: boolean;
        stripeCustomerId?: string;
    };
    idToken?: string;
}

export default function SessionDebug() {
    const { data, status } = useSession();
    // Use a type assertion to tell TypeScript about our extended session
    const session = data as ExtendedSession | null;

    const [isOpen, setIsOpen] = useState(false);

    // The layouts gate this on ENV.isDevelopment, which a deployed build can believe
    // (ENVIRONMENT falls back to 'development' when CI does not set it, M4S-5593).
    // NODE_ENV is 'production' in every build, so the session dump never ships.
    if (process.env.NODE_ENV === 'production') return null;

    return (
        <>
            <Button
                isIconOnly
                color="primary"
                variant="flat"
                className="fixed bottom-28 right-0 z-50 h-12 w-8 rounded-l-lg rounded-r-none bg-primary/10 hover:bg-primary/20"
                onPress={() => setIsOpen(true)}
                aria-label="Open Debug Panel"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-4 w-4 -rotate-180"
                >
                    <path
                        fillRule="evenodd"
                        d="M11.03 3.97a.75.75 0 0 1 0 1.06l-6.22 6.22H21a.75.75 0 0 1 0 1.5H4.81l6.22 6.22a.75.75 0 1 1-1.06 1.06l-7.5-7.5a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 0 1 1.06 0Z"
                        clipRule="evenodd"
                    />
                </svg>
            </Button>

            <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} placement="right" size="lg">
                <DrawerContent>
                    <DrawerHeader className="border-b px-4 py-3">
                        <h3 className="text-xlarge font-semibold">Session Debug Information</h3>
                        <p className="text-small text-default-500">
                            Status: {status} | User: {session?.user?.email || 'None'}
                        </p>
                    </DrawerHeader>

                    <DrawerBody className="overflow-y-auto p-4">
                        <Table
                            removeWrapper
                            aria-label="Session Debug Table"
                            className="min-w-full"
                        >
                            <TableHeader>
                                <TableColumn>Property</TableColumn>
                                <TableColumn>Value</TableColumn>
                            </TableHeader>
                            <TableBody>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>{status}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Has Session</TableCell>
                                    <TableCell>{!!session ? 'Yes' : 'No'}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>isInternal</TableCell>
                                    <TableCell>
                                        {session?.user?.isInternal !== undefined
                                            ? String(session.user.isInternal)
                                            : 'None'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>ID Token</TableCell>
                                    <TableCell className="break-all">
                                        {session?.idToken || 'None'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>User ID</TableCell>
                                    <TableCell>{session?.user?.id || 'None'}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>User Email</TableCell>
                                    <TableCell>{session?.user?.email || 'None'}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Stripe Customer ID</TableCell>
                                    <TableCell>
                                        {session?.user?.stripeCustomerId || 'None'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>User Role</TableCell>
                                    <TableCell>{session?.user?.role || 'None'}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Is Admin</TableCell>
                                    <TableCell>
                                        {session?.user?.isAdmin !== undefined
                                            ? String(session.user.isAdmin)
                                            : 'None'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Groups</TableCell>
                                    <TableCell>
                                        {session?.user?.groups?.join(', ') || 'None'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Raw Session Data</TableCell>
                                    <TableCell className="break-all">
                                        <pre className="whitespace-pre-wrap">
                                            {JSON.stringify(session, null, 2)}
                                        </pre>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    );
}
